/**
 * 阿里云 H5 图形验证码封装
 * 请在下方配置中填入控制台创建图形认证方案后生成的 appId
 */

// ========== 图形验证码配置（直接在此修改） ==========
/** 图形认证方案 appId，从阿里云控制台「图形认证方案管理」创建方案后获取 */
const CAPTCHA_APP_ID = "abba1de738542cbb4a47770c1728c79a";
/** ct4.js 脚本地址 */
const CAPTCHA_SCRIPT_URL = "https://cdn.ringdata.com/js/captcha/ct4.js";
// ==================================================

let captchaInstance = null;
let initPromise = null;

/**
 * 将 getValidate() 返回的 snake_case 转为小写驼峰，供发码接口入参使用
 */
function toCamelCaseValidate(result) {
  if (!result || result === false) return null;
  return {
    lotNumber: result.lot_number,
    captchaOutput: result.captcha_output,
    passToken: result.pass_token,
    genTime: result.gen_time,
  };
}

/**
 * 动态加载 ct4.js
 */
function loadScript(url) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("aliCaptcha only runs in browser"));
      return;
    }
    const existing = document.querySelector(`script[src="${url}"]`);
    if (existing) {
      if (window.initAlicom4) return resolve();
      existing.addEventListener("load", () => resolve());
      return;
    }
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load ali captcha script"));
    document.head.appendChild(script);
  });
}

/**
 * 获取阿里云图形验证码实例（单例）
 * @param {string} [captchaId] - 图形认证方案 appId，不传则使用上方 CAPTCHA_APP_ID
 * @param {string} [scriptUrl] - ct4.js 地址，不传则使用上方 CAPTCHA_SCRIPT_URL
 * @returns {Promise<{ showCaptcha: (onSuccess: (data: { lotNumber, captchaOutput, passToken, genTime }) => void) => void, reset: () => void }>}
 */
export function getAliCaptchaInstance(captchaId, scriptUrl) {
  const appId = captchaId || CAPTCHA_APP_ID;
  const url = scriptUrl || CAPTCHA_SCRIPT_URL;

  if (captchaInstance) {
    return Promise.resolve(captchaInstance);
  }

  if (initPromise) return initPromise;

  initPromise = loadScript(url)
    .then(() => {
      if (typeof window.initAlicom4 !== "function") {
        throw new Error(
          "initAlicom4 not found. Please ensure ct4.js is loaded."
        );
      }
      return new Promise((resolve, reject) => {
        window.initAlicom4(
          {
            captchaId: appId,
            product: "bind",
          },
          (captchaObj) => {
            if (!captchaObj) {
              reject(new Error("aliCaptcha init failed"));
              return;
            }
            let currentOnSuccess = null;
            captchaObj.onSuccess(() => {
              const result = captchaObj.getValidate();
              const camel = toCamelCaseValidate(result);
              if (camel && typeof currentOnSuccess === "function")
                currentOnSuccess(camel);
            });
            captchaObj.onNextReady(() => {
              captchaInstance = {
                _obj: captchaObj,
                showCaptcha(onSuccess) {
                  currentOnSuccess = onSuccess;
                  captchaObj.showCaptcha();
                },
                reset() {
                  if (captchaObj && typeof captchaObj.reset === "function") {
                    captchaObj.reset();
                  }
                },
              };
              resolve(captchaInstance);
            });
          }
        );
      });
    })
    .then((instance) => instance)
    .catch((err) => {
      initPromise = null;
      return Promise.reject(err);
    });

  return initPromise;
}
